import React, { useEffect, useRef, useState } from "react";
import Button from "../ui/button/Button";
import { Document, Page, pdfjs } from 'react-pdf/dist/esm/entry.webpack5';
import { documentsService } from "../../api/documentsService/documentsService";
import Spinner from "../ui/loaders/spinner/Spinner";
import { useTranslation } from "react-i18next";
import { base64ToBlob } from "../../utils/helpers/base64ToBlob";
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

type Props = {
    document: any;
    callbackCloseModal: () => void;
}

const ModalPdfDocuments: React.FC<Props> = ({
    document,
    callbackCloseModal
}) => {
    const { t } = useTranslation();

    const mainContentRef = useRef<null | HTMLDivElement>(null);

    const [docType, setDocType] = useState<null | "docx" | "pdf" | "png" | "jpg">(null);
    const [fileUrl, setFileUrl] = useState<string | null>(null);
    const [numPages, setNumPages] = useState<null | number>(null)
    const [pageNumber, setPageNumber] = useState(1);
    const [pdfWidth, setPdfWidth] = useState<null | number>(null);
    const [loading, setLoading] = useState(false);
    const [errorPdfApi, setErrorPdfApi] = useState(false);

    const canGo = useRef(true);
    useEffect(() => {
        if (canGo.current === true) {
            canGo.current = false;
            initDocument();

            if (mainContentRef.current) {
                const computedStyle = getComputedStyle(mainContentRef.current);
                const paddingHorizontal = parseFloat(computedStyle.paddingLeft) + parseFloat(computedStyle.paddingRight);
                setPdfWidth(mainContentRef.current.offsetWidth - paddingHorizontal);
            }
        }
    }, []);

    const initDocument = async () => {
        setLoading(true);
        const pdfBase64Response = await documentsService.getAttachment(document.id);

        if (pdfBase64Response.success) {
            let docType: null | "png" | "jpg" | "docx" | "pdf" = null;
            if (pdfBase64Response.responseBody.fileName.includes('.pdf')) {
                docType = 'pdf';
            } else if (pdfBase64Response.responseBody.fileName.includes('.docx')) {
                docType = 'docx';
            } else if (pdfBase64Response.responseBody.fileName.includes('.png')) {
                docType = 'png';
            } else if (pdfBase64Response.responseBody.fileName.includes('.jpg')) {
                docType = 'jpg';
            }

            setDocType(docType);

            const blobObj = base64ToBlob(pdfBase64Response.responseBody.file, docType);

            if (blobObj.success && blobObj.file !== null) {
                const url = URL.createObjectURL(blobObj.file);

                setFileUrl(url);
            } else {
                setErrorPdfApi(true);
            }
        } else {
            console.log('error get attachment of document');
            setErrorPdfApi(true);
        }

        setLoading(false);
    }

    const nextPage = () => {
        if (numPages && pageNumber < numPages) {
            setPageNumber(pageNumber + 1);
        }
    }

    const prevPage = () => {
        if (pageNumber > 1) {
            setPageNumber(pageNumber - 1);
        }
    }

    return (
        <>
            {
                !!document &&
                <div className="fixed z-[999] top-0 left-0 h-[100dvh] w-screen bg-[#00000080] flex items-center justify-center">
                    <div className="bg-white h-[95%] w-[95%] lg:w-[50%] max-w-[700px] flex flex-col">
                        <div className="shrink-0 flex justify-between border-b p-[30px]">
                            <div className="text-lg font-bold leading-[21.6px]">
                                {document.name}
                            </div>
                            <div onClick={callbackCloseModal} className="cursor-pointer">
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-8 h-8">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </div>
                        </div>

                        {/* MAIN CONTENT */}
                        <div ref={mainContentRef} className="grow py-8 px-[30px] select-none overflow-auto relative">
                            {
                                loading === false && !!docType && !!fileUrl && !errorPdfApi && pdfWidth !== null &&
                                <>
                                    {
                                        docType === 'pdf' &&
                                        <>
                                            <Document file={fileUrl} onLoadSuccess={({ numPages }) => setNumPages(numPages)}>
                                                <Page width={pdfWidth} renderTextLayer={false} renderAnnotationLayer={false} pageNumber={pageNumber} />
                                            </Document>

                                            {
                                                !!pageNumber && !!numPages &&
                                                <div className="sticky flex items-center w-max rounded-[8px] bottom-0 left-[50%] -translate-x-[50%] z-10 bg-white shadow p-4">
                                                    <div onClick={prevPage} className={"" + (pageNumber > 1 ? ' cursor-pointer' : ' opacity-50')}>
                                                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
                                                            <path fillRule="evenodd" d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z" clipRule="evenodd" />
                                                        </svg>
                                                    </div>
                                                    <div className="text-sm mx-1 mb-[2px]">{pageNumber} / {numPages}</div>
                                                    <div onClick={nextPage} className={"" + (numPages && pageNumber < numPages ? ' cursor-pointer' : ' opacity-50')}>
                                                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
                                                            <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
                                                        </svg>

                                                    </div>
                                                </div>
                                            }
                                        </>
                                    }

                                    {
                                        (docType === 'jpg' || docType === 'png') &&
                                        <img src={fileUrl} className="block w-full h-auto mb-4" alt="filename" />
                                    }

                                    {
                                        docType === 'docx' &&
                                        <div>
                                            {t('components.modalPdfContracts.previewNotDocx')}
                                        </div>
                                    }

                                    {
                                        <div>
                                            <a className="text-sm underline text-red" href={fileUrl} target="_blank">
                                                {
                                                    (docType === "pdf" || docType === "jpg" || docType === "png") &&
                                                    t('components.modalPdfDocuments.viewFileFullPage')
                                                }

                                                {
                                                    docType === "docx" &&
                                                    t('components.modalPdfDocuments.download')
                                                }
                                            </a>
                                        </div>
                                    }
                                </>
                            }

                            {
                                loading === true &&
                                <div className="flex items-center">
                                    <Spinner />
                                    <div className="ml-2">{t('components.modalPdfDocuments.loading')}</div>
                                </div>
                            }

                            {
                                loading === false && errorPdfApi === true &&
                                <div>
                                    {t('components.modalPdfDocuments.errorLoadDocument')}
                                </div>
                            }
                        </div>

                        <div className="select-none px-[30px] py-6 border-t flex items-center justify-between">
                            {/* Acceptance checkbox space */}
                            <div>

                            </div>

                            <Button
                                callback={() => callbackCloseModal()}
                            >
                                {t('components.modalPdfDocuments.confirmButton')}
                            </Button>
                        </div>

                    </div>
                </div>
            }
        </>
    );
}

export default ModalPdfDocuments;